import React, { useState, useEffect, useRef } from "react";
import { Interview } from "@/services/interviewService";
import { interviewService } from "@/services/serviceFactory";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import { EditingCell, InterviewTableProps } from "./types";
import EditableCell from "./EditableCell";
import ActionButtons from "./ActionButtons";
import TableSkeleton from "./TableSkeleton";
import AddInterviewModal from "./AddInterviewModal";
import { aiService } from "@/services/serviceFactory";
import { useDebounce } from "@/hooks/useDebounce";
import {
  ConstrainedAlertDialog as AlertDialog,
  ConstrainedAlertDialogAction as AlertDialogAction,
  ConstrainedAlertDialogCancel as AlertDialogCancel,
  ConstrainedAlertDialogContent as AlertDialogContent,
  ConstrainedAlertDialogDescription as AlertDialogDescription,
  ConstrainedAlertDialogFooter as AlertDialogFooter,
  ConstrainedAlertDialogHeader as AlertDialogHeader,
  ConstrainedAlertDialogTitle as AlertDialogTitle,
} from "@/components/ui/constrained-alert-dialog";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  getFilteredRowModel,
  filterFns,
  getFacetedUniqueValues,
  SortingState,
} from "@tanstack/react-table";
import { addGlobalStyles, createColumns, multiSelectFilter } from "./columns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Eye, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StarRatingCellRef } from "./StarRatingCell";
import { TranscriptStatusCellRef } from "./TranscriptStatusCell";
import { TranscriptUploadModal } from "./TranscriptUploadModal";
import { Transcript } from "@/services/transcriptService";
import { track } from "@vercel/analytics";

// Define default sorting (by date in descending order)
const initialSortingState: SortingState = [
  {
    id: "date",
    desc: true,
  },
];

// localStorage key for column visibility
const COLUMN_VISIBILITY_KEY = "interview-table-column-visibility";

// Helper function to normalize interviews for persistence
function normalizeForPersistence(interviews: Interview[]): Interview[] {
  return interviews.map((interview) => ({
    ...interview,
    initialInterviewCost:
      typeof interview.initialInterviewCost === "string" &&
      interview.initialInterviewCost === ""
        ? "0"
        : String(Number(interview.initialInterviewCost)),
    duration:
      typeof interview.duration === "string" && interview.duration === ""
        ? "0"
        : String(Number(interview.duration)),
  }));
}

const InterviewTable: React.FC<InterviewTableProps> = ({
  interviews,
  isLoading = false,
  onInterviewUpdate,
  onRefresh,
  projectId: tableProjId,
  recentlySyncedIds = [],
  currentUserRole = "viewer",
  onOpenChat,
}) => {
  // State to track which field is being edited
  const [editingCell, setEditingCell] = useState<EditingCell>({
    id: "",
    field: null,
  });

  // Add a ref for the table component to track cells
  const tableRef = React.useRef<HTMLDivElement>(null);

  // Ref to store rating cell references for triggering popups
  const ratingRefs = useRef<Record<string, StarRatingCellRef>>({});

  // Ref to store transcript cell references for triggering upload modals
  const transcriptRefs = useRef<Record<string, TranscriptStatusCellRef>>({});

  // State to track the original interviews data
  const [originalInterviews, setOriginalInterviews] = useState<Interview[]>([]);

  // State to track the edited interviews
  const [editedInterviews, setEditedInterviews] = useState<Interview[]>([]);

  // State for autosaving
  const [isSaving, setIsSaving] = useState(false);
  const [changedInterviewIds, setChangedInterviewIds] = useState<Set<string>>(
    new Set()
  );

  // Debounced version of edited interviews for autosave
  const debouncedInterviews = useDebounce(editedInterviews, 800);

  // State for undo/redo functionality
  const [undoStack, setUndoStack] = useState<Interview[][]>([]);
  const [redoStack, setRedoStack] = useState<Interview[][]>([]);

  // State for row selection
  const [rowSelection, setRowSelection] = useState({});

  // State for sorting - initialize with date column in descending order
  const [sorting, setSorting] = useState<SortingState>(initialSortingState);

  // Track if date sort was intentionally activated (vs just default)
  const [dateSortActivated, setDateSortActivated] = useState(false);

  // State for column filters
  const [columnFilters, setColumnFilters] = useState([]);

  // State for column resizing
  const [columnSizing, setColumnSizing] = useState({});

  // State for column visibility - initialize from localStorage if available
  const [columnVisibility, setColumnVisibility] = useState(() => {
    try {
      const saved = localStorage.getItem(COLUMN_VISIBILITY_KEY);
      const defaultHidden = {
        questionnaireCost: false,
      };

      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with default hidden columns, prioritizing saved preferences
        return { ...defaultHidden, ...parsed };
      }

      return defaultHidden;
    } catch (error) {
      console.error(
        "Error loading column visibility from localStorage:",
        error
      );
      return {
        questionnaireCost: false,
      };
    }
  });

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // State for tracking deletion in progress
  const [isDeleting, setIsDeleting] = useState(false);

  // State to track recently synced interview IDs (frontend-only, temporary)
  const [recentlySyncedIdsSet, setRecentlySyncedIdsSet] = useState<Set<string>>(
    new Set(recentlySyncedIds)
  );

  // State for add interview modal
  const [addInterviewModalOpen, setAddInterviewModalOpen] = useState(false);

  // Hoisted Transcript modal state
  const [transcriptModalOpen, setTranscriptModalOpen] = useState(false);
  const [transcriptModalInterview, setTranscriptModalInterview] =
    useState<Interview | null>(null);
  const [transcriptModalMode, setTranscriptModalMode] = useState<
    "upload" | "view"
  >("upload");
  const [transcriptModalExisting, setTranscriptModalExisting] =
    useState<Transcript | null>(null);

  // Ref to track if we're in an immediate save operation (to prevent conflicting autosaves)
  const isImmediateSavingRef = useRef(false);

  // Ref to track recently immediately-saved interview IDs (to exclude from autosave)
  const recentlyImmediateSavedRef = useRef<Set<string>>(new Set());

  // Ref to track which interviews are currently being immediately saved (to prevent duplicate calls)
  const currentlyImmediateSavingIds = useRef<Set<string>>(new Set());

  // Create columns with the editable cell renderer
  const columns = React.useMemo(() => createColumns(onOpenChat), [onOpenChat]);

  // Initialize original and edited interviews when the interviews prop changes
  useEffect(() => {
    // Filter out secondary reports as they're managed in the overview section
    const filteredInterviews = interviews.filter(
      (i) => i.status !== "secondary report"
    );
    setOriginalInterviews(filteredInterviews);
    setEditedInterviews(filteredInterviews);
    setChangedInterviewIds(new Set()); // Clear changed tracking when data refreshes
  }, [interviews]);

  // Update recently synced IDs when prop changes
  useEffect(() => {
    if (recentlySyncedIds.length > 0) {
      setRecentlySyncedIdsSet(new Set(recentlySyncedIds));
    }
  }, [recentlySyncedIds]);

  // Add a useEffect to inject the global styles
  useEffect(() => {
    addGlobalStyles();
  }, []);

  // Update interview data
  const updateData = async (
    id: string,
    field: keyof Interview,
    value: any,
    skipTranscriptModal: boolean = false
  ) => {
    // Get the current interview to check previous status and rating
    const currentInterview = editedInterviews.find(
      (interview) => interview.id === id
    );

    // Special handling for date fields to ensure they're properly processed
    if (field === "date" && typeof value === "string") {
      // Ensure the date string is a valid ISO string
      try {
        const dateObj = new Date(value);
        if (isNaN(dateObj.getTime())) {
          console.error("Invalid date value:", value);
          return; // Don't update with invalid date
        }
        // Convert the ISO string back to a Date object to maintain consistency
        // with the original data structure (dates are loaded as Date objects)
        value = dateObj;
      } catch (e) {
        console.error("Error parsing date:", e);
        return; // Don't update with invalid date
      }
    }

    // Check if status changed to "completed" or "follow-up completed" and no transcript exists
    // Save immediately before opening modal to avoid refresh issues
    // Skip if this is being called from a transcript upload context
    const shouldOpenTranscriptModal =
      !skipTranscriptModal &&
      field === "status" &&
      (value === "completed" || value === "follow-up completed") &&
      currentInterview &&
      currentInterview.status !== value &&
      !currentInterview.transcriptStatus;

    setEditedInterviews((prev) => {
      const newInterviews = prev.map((interview) => {
        if (interview.id === id) {
          if (field === "duration") {
            const nextDurationNum = Number(value || "0");
            return {
              ...interview,
              duration: String(nextDurationNum),
            };
          }

          return { ...interview, [field]: value };
        }
        return interview;
      });

      // Record change for undo functionality (only if there's a real change)
      if (JSON.stringify(prev) !== JSON.stringify(newInterviews)) {
        setUndoStack((stack) => [...stack.slice(-49), prev]); // Keep max 50 snapshots
        setRedoStack([]); // Clear redo stack when new change is made

        // Track which interview was changed for delta updates
        setChangedInterviewIds((changed) => new Set(changed).add(id));
      }

      // Force re-evaluation by returning a new array
      return [...newInterviews];
    });

    // Check if status changed to "completed" and no rating exists
    if (
      field === "status" &&
      value === "completed" &&
      currentInterview &&
      currentInterview.status !== "completed" &&
      !currentInterview.expertRating
    ) {
      // Delay to ensure the table has re-rendered with the new data
      setTimeout(() => {
        const ratingRef = ratingRefs.current[id];
        if (ratingRef) {
          ratingRef.openRatingPopup();
        }
      }, 100);
    }

    // Handle transcript modal opening - save first, then open modal
    if (shouldOpenTranscriptModal) {
      // Guard against duplicate immediate saves for the same interview
      if (currentlyImmediateSavingIds.current.has(id)) {
        return;
      }

      // Mark this interview as currently being saved
      currentlyImmediateSavingIds.current.add(id);

      // Show toast notification immediately
      toast({
        title: "Upload Transcript?",
        description: "Upload a transcript to make this interview searchable.",
        duration: 4000,
      });

      // Set flag to prevent autosave from interfering
      isImmediateSavingRef.current = true;

      // Add to recently saved set to exclude from autosave
      recentlyImmediateSavedRef.current.add(id);

      // Clear from recently saved set after 3 seconds (enough for async ops to complete)
      setTimeout(() => {
        recentlyImmediateSavedRef.current.delete(id);
      }, 3000);

      // Save the change immediately
      const interviewToSave = editedInterviews.find((int) => int.id === id);
      if (interviewToSave) {
        const updatedInterview = { ...interviewToSave, [field]: value };
        const normalized = normalizeForPersistence([updatedInterview]);

        try {
          await onInterviewUpdate?.(normalized);
          // Clear the changed tracking for this interview since we just saved it
          setChangedInterviewIds((changed) => {
            const newSet = new Set(changed);
            newSet.delete(id);
            return newSet;
          });

          // Now open the modal using ref (data is fresh from save)
          setTimeout(() => {
            // Open hoisted transcript modal (upload mode)
            const intv =
              editedInterviews.find((i) => i.id === id) || updatedInterview;
            setTranscriptModalInterview(intv as Interview);
            setTranscriptModalMode("upload");
            setTranscriptModalExisting(null);
            setTranscriptModalOpen(true);
            // Reset flags after modal opens
            isImmediateSavingRef.current = false;
            currentlyImmediateSavingIds.current.delete(id);
          }, 50);
        } catch (error) {
          console.error(
            "Failed to save before opening transcript modal:",
            error
          );
          // Still open the modal even if save failed
          setTimeout(() => {
            const intv =
              editedInterviews.find((i) => i.id === id) || interviewToSave;
            setTranscriptModalInterview(intv as Interview);
            setTranscriptModalMode("upload");
            setTranscriptModalExisting(null);
            setTranscriptModalOpen(true);
            // Reset flags after modal opens
            isImmediateSavingRef.current = false;
            currentlyImmediateSavingIds.current.delete(id);
          }, 50);
        }
      }
    }

    // Auto-recalculate anonymized attribution when company or title changes
    if (field === "company" || field === "title") {
      // Determine latest title and company values after this update
      const latestTitle = (
        field === "title" ? value : currentInterview?.title
      ) as string | undefined;
      const latestCompany = (
        field === "company" ? value : currentInterview?.company
      ) as string | undefined;

      if (latestTitle && latestCompany) {
        // Generate asynchronously without blocking UI
        (async () => {
          try {
            const anonymizedAttribution =
              await aiService.generateAnonymizedAttribution(
                latestTitle,
                latestCompany
              );

            if (anonymizedAttribution) {
              setEditedInterviews((prev) => {
                const updated = prev.map((interview) =>
                  interview.id === id
                    ? { ...interview, anonymizedAttribution }
                    : interview
                );
                return [...updated];
              });
              setChangedInterviewIds((changed) => new Set(changed).add(id));
            }
          } catch (e) {
            console.error("Failed to auto-generate anonymized attribution:", e);
          }
        })();
      }
    }

    // Auto-recalculate initial interview cost when duration changes using backend pricing helper
    if (field === "duration") {
      const nextDurationNum = Number(value || "0");
      const network = currentInterview?.network || "";
      if (network && nextDurationNum >= 0) {
        (async () => {
          try {
            const calculated = await aiService.calculatePrice(
              network,
              nextDurationNum,
              undefined,
              currentInterview?.initialInterviewCost,
              currentInterview?.duration
            );
            if (calculated !== null) {
              setEditedInterviews((prev) => {
                const updated = prev.map((intv) =>
                  intv.id === id
                    ? { ...intv, initialInterviewCost: String(calculated) }
                    : intv
                );
                return [...updated];
              });
              setChangedInterviewIds((changed) => new Set(changed).add(id));
            }
          } catch (e) {
            console.error("Failed to calculate price:", e);
          }
        })();
      }
    }
  };

  // Autosave effect - triggered when debounced interviews change
  useEffect(() => {
    // Don't save on initial mount or if no changes
    if (changedInterviewIds.size === 0) {
      return;
    }

    // Skip autosave if we're in the middle of an immediate save
    if (isImmediateSavingRef.current) {
      return;
    }

    const autosave = async () => {
      setIsSaving(true);
      try {
        // ✅ Use current editedInterviews (not stale debouncedInterviews) when filtering
        // Exclude interviews that were recently immediately saved
        const changedInterviews = editedInterviews.filter(
          (interview) =>
            changedInterviewIds.has(interview.id) &&
            !recentlyImmediateSavedRef.current.has(interview.id)
        );

        if (changedInterviews.length === 0) {
          setIsSaving(false);
          return;
        }

        const normalized = normalizeForPersistence(changedInterviews);
        const result = await onInterviewUpdate?.(normalized);

        // Handle both old boolean return and new object return
        const isSuccess =
          typeof result === "boolean" ? result : result?.success;
        const errorMessage =
          typeof result === "object" && result?.error
            ? result.error
            : "Changes kept locally. Check your connection.";

        if (isSuccess) {
          setOriginalInterviews(editedInterviews); // Use current editedInterviews
          setChangedInterviewIds(new Set()); // Clear changed tracking
          // Clear recently synced interview IDs
          setRecentlySyncedIdsSet(new Set());
        } else {
          toast({
            title: "Autosave failed",
            description: errorMessage,
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Autosave error:", error);
        toast({
          title: "Autosave failed",
          description: "Changes kept locally. Check your connection.",
          variant: "destructive",
        });
      } finally {
        setIsSaving(false);
      }
    };

    autosave();
  }, [
    debouncedInterviews,
    changedInterviewIds,
    editedInterviews,
    onInterviewUpdate,
  ]);

  // Get current project ID from props only
  const getCurrentProjectId = (): string | null => {
    // Use project ID from props
    if (tableProjId) {
      return tableProjId;
    }
    return null;
  };

  // Keyboard event listener for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;

      if (!isCtrlOrCmd || e.key !== "z") return;

      const isUndo = !e.shiftKey;
      const isRedo = e.shiftKey;

      if (isUndo && undoStack.length > 0) {
        e.preventDefault();
        setRedoStack((stack) => [...stack, editedInterviews]);
        const previousState = undoStack[undoStack.length - 1];
        setUndoStack((stack) => stack.slice(0, -1));
        setEditedInterviews(previousState);

        // Mark all interviews as changed to trigger autosave
        const changedIds = new Set(
          previousState.map((interview) => interview.id)
        );
        setChangedInterviewIds(changedIds);
      }

      if (isRedo && redoStack.length > 0) {
        e.preventDefault();
        setUndoStack((stack) => [...stack, editedInterviews]);
        const nextState = redoStack[redoStack.length - 1];
        setRedoStack((stack) => stack.slice(0, -1));
        setEditedInterviews(nextState);

        // Mark all interviews as changed to trigger autosave
        const changedIds = new Set(nextState.map((interview) => interview.id));
        setChangedInterviewIds(changedIds);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [undoStack, redoStack, editedInterviews]);

  // Check for unsaved changes
  // useEffect(() => {
  //   const hasChanges =
  //     JSON.stringify(originalInterviews) !== JSON.stringify(editedInterviews);
  //   setHasUnsavedChanges(hasChanges);
  // }, [originalInterviews, editedInterviews]);

  // Add a new interview through the modal
  const handleAddInterview = () => {
    setAddInterviewModalOpen(true);
  };

  // Handle modal submit
  const handleModalSubmit = async (interviewData: Partial<Interview>) => {
    // Get the current project ID
    const projectId = getCurrentProjectId();

    if (!projectId) {
      toast({
        title: "Error",
        description: "No project selected. Please select a project first.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Create the interview immediately without AI-generated fields
      const newInterview = await interviewService.createInterview(
        interviewData,
        projectId
      );

      if (newInterview) {
        // Add new interview to the list immediately
        const updatedInterviews = [...editedInterviews, newInterview];
        setEditedInterviews(updatedInterviews);
        setOriginalInterviews(updatedInterviews);

        // Notify parent component to refresh data and update statistics
        if (onInterviewUpdate) {
          await onInterviewUpdate(updatedInterviews);
        }

        toast({
          title: "Success",
          description: "New interview added successfully.",
        });

        // Generate AI fields in the background if we have the required data
        if (interviewData.title && interviewData.company) {
          generateAIFieldsInBackground(newInterview, interviewData);
        }
      } else {
        toast({
          title: "Error",
          description: "Failed to add new interview.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error adding interview:", error);
      toast({
        title: "Error",
        description: "An error occurred while adding the interview.",
        variant: "destructive",
      });
    }
  };

  // Generate AI fields in the background and update the interview
  const generateAIFieldsInBackground = async (
    interview: Interview,
    originalData: Partial<Interview>
  ) => {
    try {
      let updatedInterview = { ...interview };
      let hasUpdates = false;

      // Generate anonymized attribution
      const anonymizedAttribution =
        await aiService.generateAnonymizedAttribution(
          originalData.title!,
          originalData.company!
        );

      if (anonymizedAttribution) {
        updatedInterview.anonymizedAttribution = anonymizedAttribution;
        hasUpdates = true;

        // Generate interview description if we have all required fields
        if (
          originalData.date &&
          originalData.expertFirstName &&
          originalData.expertLastName
        ) {
          const interviewDescription = aiService.generateInterviewDescription(
            originalData.date,
            originalData.expertFirstName,
            originalData.expertLastName,
            anonymizedAttribution
          );
          updatedInterview.interviewDescription = interviewDescription;
        }
      }

      // Update the interview in the database if we have AI-generated content
      if (hasUpdates) {
        // Update via the interview service
        const success = await interviewService.updateInterview(
          updatedInterview
        );

        if (success) {
          // Update the local state to reflect the AI-generated fields
          setEditedInterviews((prev) =>
            prev.map((int) =>
              int.id === interview.id ? updatedInterview : int
            )
          );
          setOriginalInterviews((prev) =>
            prev.map((int) =>
              int.id === interview.id ? updatedInterview : int
            )
          );

          toast({
            title: "AI Generation Complete",
            description:
              "Anonymized attribution and description have been generated.",
          });
        }
      }
    } catch (error) {
      console.error("Error generating AI fields in background:", error);
      // Don't show error toast for background operations
      // The interview was already created successfully
    }
  };

  // Delete selected interviews
  const handleDeleteInterview = () => {
    const selectedIds = Object.keys(rowSelection).filter(
      (id) => rowSelection[id]
    );

    if (selectedIds.length === 0) {
      toast({
        title: "No interviews selected",
        description: "Please select at least one interview to delete.",
        variant: "destructive",
      });
      return;
    }

    // Open the confirmation dialog
    setDeleteDialogOpen(true);
  };

  // Confirm deletion of selected interviews
  const confirmDeleteInterviews = async () => {
    const selectedRowIds = Object.keys(rowSelection).filter(
      (id) => rowSelection[id]
    );

    setIsDeleting(true);

    try {
      // Map row IDs to actual interview objects using table rows for accuracy
      const selectedInterviews = [];
      for (const rowId of selectedRowIds) {
        const tableRow = table.getRowModel().rows.find((r) => r.id === rowId);
        if (tableRow) {
          selectedInterviews.push(tableRow.original);
        }
      }

      // Track deletion progress
      let successCount = 0;
      let failureCount = 0;

      // Delete interviews sequentially
      for (const interview of selectedInterviews) {
        const success = await interviewService.deleteInterview(interview.id);
        if (success) {
          successCount++;
        } else {
          failureCount++;
        }
      }

      // Handle the results
      if (failureCount === 0 && successCount > 0) {
        // Track successful deletion
        track("interviews_deleted", {
          count: successCount,
          project_id: tableProjId || "unknown",
        });

        // Clear row selection
        setRowSelection({});

        // Refresh data from backend to ensure consistency
        if (onRefresh) {
          await onRefresh();
        }

        toast({
          title: "Success",
          description: `Successfully deleted ${successCount} interview(s).`,
        });
      } else if (successCount > 0) {
        // Track partial deletion
        track("interviews_deleted", {
          count: successCount,
          failed_count: failureCount,
          project_id: tableProjId || "unknown",
        });

        // Clear row selection
        setRowSelection({});

        // Refresh data from backend to ensure consistency
        if (onRefresh) {
          await onRefresh();
        }

        toast({
          title: "Partial Success",
          description: `${successCount} interviews deleted, ${failureCount} failed.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Could not delete the selected interviews.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error deleting interviews:", error);
      toast({
        title: "Error",
        description: "An error occurred while deleting interviews.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // Save column visibility to localStorage when it changes
  const handleColumnVisibilityChange = (updater: any) => {
    setColumnVisibility((prev: any) => {
      const newVisibility =
        typeof updater === "function" ? updater(prev) : updater;

      try {
        localStorage.setItem(
          COLUMN_VISIBILITY_KEY,
          JSON.stringify(newVisibility)
        );
      } catch (error) {
        console.error("Error saving column visibility to localStorage:", error);
      }

      return newVisibility;
    });
  };

  const table = useReactTable({
    data: editedInterviews,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    filterFns: {
      includesString: filterFns.includesString,
      multiSelectFilter: multiSelectFilter,
    },
    enableRowSelection: true,
    enableColumnFilters: true,
    enableSorting: true,
    enableMultiSort: true, // Enable multi-column sorting with Shift-click
    enableHiding: true,
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
      sorting,
      columnFilters,
      columnSizing,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    columnResizeMode: "onChange",
    getFacetedUniqueValues: getFacetedUniqueValues(),
    meta: {
      updateData,
      onUpdate: updateData,
      setEditingCell,
      recentlySyncedIds: recentlySyncedIdsSet,
      currentUserRole,
      ratingRefs,
      transcriptRefs,
      dateSortActivated,
      setDateSortActivated,
      onOpenTranscriptModal: (args: {
        interview: Interview;
        mode: "upload" | "view";
        existingTranscript?: Transcript | null;
      }) => {
        setTranscriptModalInterview(args.interview);
        setTranscriptModalMode(args.mode);
        setTranscriptModalExisting(args.existingTranscript || null);
        setTranscriptModalOpen(true);
      },
    },
    getRowId: (row) => row.id,
  });

  if (isLoading) {
    return <TableSkeleton />;
  }

  return (
    <div className="space-y-4">
      {/* Header row with tip and action buttons */}
      <div className="flex items-center mb-4">
        {/* Table instructions - will compress if needed */}
        <div className="text-sm text-gray-500 dark:text-gray-400 truncate pr-4 flex-1">
          {currentUserRole === "viewer"
            ? "You have view-only access to this project. Contact the project owner to request edit permissions."
            : "All changes are saved automatically. Click on any cell to edit interview data. Press Ctrl + Z to undo, Shift + Ctrl + Z to redo."}
        </div>

        {/* Action buttons - always on the right */}
        <div className="flex-shrink-0">
          <ActionButtons
            onAddInterview={
              currentUserRole !== "viewer" ? handleAddInterview : undefined
            }
            onDeleteInterview={
              currentUserRole !== "viewer" ? handleDeleteInterview : undefined
            }
            canDelete={
              currentUserRole !== "viewer" &&
              Object.keys(rowSelection).some((id) => rowSelection[id])
            }
            isSaving={isSaving}
            isDeleting={isDeleting}
          />
        </div>
      </div>

      {/* Interview table */}
      <div
        ref={tableRef}
        className="overflow-x-auto border rounded-md bg-white dark:bg-gray-800 dark:border-gray-700"
      >
        {/* Column visibility toggle */}
        {(table
          .getAllColumns()
          .some((col) => !col.getIsVisible() && col.id !== "select") ||
          (() => {
            // Check if filters or sorting differ from default
            const hasFilters = table.getState().columnFilters.length > 0;
            const currentSorting = table.getState().sorting;
            const isDefaultSort =
              currentSorting.length === 1 &&
              currentSorting[0].id === "date" &&
              currentSorting[0].desc === true;
            return hasFilters || !isDefaultSort;
          })()) && (
          <div className="p-3 border-b bg-gray-50 dark:bg-gray-700 dark:border-gray-600 flex items-center gap-2">
            {/* Clear Filters/Sorts Button */}
            {(() => {
              const hasFilters = table.getState().columnFilters.length > 0;
              const currentSorting = table.getState().sorting;
              const isDefaultSort =
                currentSorting.length === 1 &&
                currentSorting[0].id === "date" &&
                currentSorting[0].desc === true;
              const showClearButton = hasFilters || !isDefaultSort;

              return (
                showClearButton && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Clear all filters
                      table.resetColumnFilters();
                      // Reset to default sort (date descending)
                      setSorting(initialSortingState);
                      // Reset the date sort activation flag
                      setDateSortActivated(false);
                    }}
                    className="h-8 gap-2 bg-white dark:bg-gray-800 border-orange-300 dark:border-orange-600 text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:border-orange-400 dark:hover:border-orange-500 hover:text-orange-900 dark:hover:text-orange-100 font-normal"
                  >
                    <X className="h-4 w-4" />
                    Clear Filters/Sorts
                  </Button>
                )
              );
            })()}

            {/* Show Hidden Columns Dropdown */}
            {table
              .getAllColumns()
              .some((col) => !col.getIsVisible() && col.id !== "select") && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-900 dark:hover:text-gray-100 font-normal"
                  >
                    Show hidden columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  collisionPadding={8}
                  className="w-48 max-h-[500px] overflow-y-auto"
                >
                  {table
                    .getAllColumns()
                    .filter(
                      (column) => column.getCanHide() && column.id !== "select"
                    )
                    .map((column) => {
                      // Get the title from the column definition
                      const getColumnTitle = (column: any) => {
                        const titleMap: Record<string, string> = {
                          anonymizedAttribution: "Anonymized Attribution",
                          interviewDescription: "Interview Description",
                          expertFirstName: "First Name",
                          expertLastName: "Last Name",
                          company: "Company",
                          title: "Title",
                          network: "Network",
                          angle: "Angle",
                          date: "Date",
                          duration: "Duration (min)",
                          initialInterviewCost: "Interview Cost",
                          questionnaireCost: "Questionnaire Cost",
                          totalCost: "Total Cost",
                          status: "Status",
                          expertRating: "Expert Rating",
                          transcript: "Transcript",
                        };
                        return titleMap[column.id] || column.id;
                      };

                      return (
                        <DropdownMenuCheckboxItem
                          key={column.id}
                          className="capitalize"
                          checked={column.getIsVisible()}
                          onCheckedChange={(value) =>
                            column.toggleVisibility(!!value)
                          }
                          onSelect={(event) => {
                            event.preventDefault();
                          }}
                        >
                          {getColumnTitle(column)}
                        </DropdownMenuCheckboxItem>
                      );
                    })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}

        <div className="relative w-full">
          <Table className="w-full table-fixed">
            <thead className="bg-gradient-to-r from-gray-50 to-ivy-light/50 dark:from-gray-700 dark:to-gray-600 border-b dark:border-gray-600">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-4 py-2 text-left text-xs font-medium text-slate-blue dark:text-gray-300 uppercase tracking-wider relative"
                      style={{
                        width: header.getSize(),
                        position: "relative",
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </>
                      )}
                      {header.column.getCanResize() && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          onClick={(e) => e.stopPropagation()}
                          className="resizer"
                          style={{
                            backgroundColor: header.column.getIsResizing()
                              ? "#5CB85C"
                              : "",
                            opacity: header.column.getIsResizing() ? 1 : "",
                          }}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={cn(
                    "hover:bg-ivy-light/30 dark:hover:bg-gray-700/50 transition-colors border-b border-gray-100 dark:border-gray-700",
                    index % 2 === 0
                      ? "bg-white dark:bg-gray-800"
                      : "bg-gray-50 dark:bg-gray-800/50"
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100"
                      style={{ width: cell.column.getSize() }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Add Interview Modal */}
      <AddInterviewModal
        isOpen={addInterviewModalOpen}
        onClose={() => setAddInterviewModalOpen(false)}
        onSubmit={handleModalSubmit}
        projectId={tableProjId}
      />

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={!isDeleting ? setDeleteDialogOpen : undefined}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isDeleting ? "Deleting Interviews..." : "Are you sure?"}
            </AlertDialogTitle>
            {isDeleting ? (
              <AlertDialogDescription asChild>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
                  <span>
                    Processing deletion. This may take a moment due to
                    transcript and embedding cleanup...
                  </span>
                </div>
              </AlertDialogDescription>
            ) : (
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the
                selected interviews.
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteInterviews}
              disabled={isDeleting}
              className={isDeleting ? "opacity-50 cursor-not-allowed" : ""}
            >
              {isDeleting ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Deleting...</span>
                </div>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Hoisted Transcript Modal */}
      {transcriptModalOpen && transcriptModalInterview && (
        <TranscriptUploadModal
          isOpen={transcriptModalOpen}
          onClose={() => setTranscriptModalOpen(false)}
          interview={transcriptModalInterview}
          onUploadSuccess={() => {
            // Update the interview status to show "processing" immediately
            if (transcriptModalInterview) {
              updateData(
                transcriptModalInterview.id,
                "transcriptStatus",
                "processing"
              );
              // Also update the interview status to completed if needed (respecting follow-up status)
              // Pass skipTranscriptModal=true to avoid reopening the modal
              if (
                transcriptModalInterview.status !== "completed" &&
                transcriptModalInterview.status !== "follow-up completed"
              ) {
                const newStatus =
                  transcriptModalInterview.status === "follow-up scheduled"
                    ? "follow-up completed"
                    : "completed";
                updateData(
                  transcriptModalInterview.id,
                  "status",
                  newStatus,
                  true
                );
              }
            }
          }}
          mode={transcriptModalMode}
          existingTranscript={transcriptModalExisting as any}
          onOpenChat={onOpenChat}
        />
      )}
    </div>
  );
};

export default InterviewTable;
